import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';

const ITEMS_PER_PAGE = 16; // Increased for better UX

async function fetchPaginatedPages({ pageParam = 0, pageType, isAllPages }) {
  const from = pageParam * ITEMS_PER_PAGE;
  const to = from + ITEMS_PER_PAGE - 1;

  // Only select needed fields for performance
  let query = supabase
    .from('seo_auto_pages')
    .select('slug, title, description, primary_type_page, secondary_type_page, updated_at', { count: 'exact' })
    .eq('status', 'published')
    .order('updated_at', { ascending: false }) // Latest posts first
    .range(from, to);

  // Filter by type if not fetching all pages
  if (!isAllPages && pageType) {
    query = query.eq('primary_type_page', pageType);
  }

  const { data, error, count } = await query;

  if (error) throw error;

  return {
    pages: data || [],
    totalCount: count || 0,
    hasMore: (from + (data?.length || 0)) < count,
    nextPage: (from + (data?.length || 0)) < count ? pageParam + 1 : undefined
  };
}

export function usePaginatedPages(pageType, isAllPages = false) {
  const { 
    data, 
    isLoading, 
    error, 
    isFetching,
    fetchNextPage,
    hasNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey: ['paginatedPages', pageType, isAllPages],
    queryFn: ({ pageParam = 0 }) => fetchPaginatedPages({ pageParam, pageType, isAllPages }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in memory for 10 minutes
    retry: 1,
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });

  const allPages = data?.pages?.flatMap(page => page.pages) || [];
  const totalCount = data?.pages?.[0]?.totalCount || 0;

  const loadMore = () => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  };

  return {
    pages: allPages,
    totalCount,
    hasMore: hasNextPage,
    isLoading,
    error,
    isFetching,
    loadMore,
    retry: refetch
  };
}